import { getApiClient } from './client';
import { getShopId } from '../utils/shopId';
import type { ShopPlanInfo, UsageAnalytics } from '../types/billing.types';
import type { GuaranteeResponse, GuaranteeStatus } from '../types/guarantee.types';
import type {
  CancellationFeedbackPayload,
  CancellationFeedbackResult,
} from '../types/cancellation.types';
import type {
  LifecycleNudge,
  NudgeDismissResponse,
  NudgeResponse,
  NudgeTriggerKey,
} from '../types/nudge.types';

export const billingApi = {
  getPlan: async (): Promise<ShopPlanInfo> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId ? `/api/me/plan?shop_id=${shopId}` : '/api/me/plan';
    
    try {
      const { data } = await client.get<ShopPlanInfo>(url);
      return data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          shop_id: shopId || '00000000-0000-0000-0000-000000000000',
          free_trial_used: false,
          free_trial_expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          subscription: null,
          plan: null,
          usage: {
            assets_generated_this_month: 0,
            max_assets_per_month: 0,
            premium_generations_used: 0,
            premium_generations_included: 0,
            billing_period_reset_at: null,
          },
          // Omitted on purpose: the callers' fallback derives the ladder from
          // `plan` (null here → first-time subscribe), which is the same
          // answer without duplicating the rules in two places.
        };
      }
      throw error;
    }
  },

  /**
   * Storefront engagement for the current billing period.
   *
   * Returns null instead of throwing on failure: this feeds a supplementary
   * panel, and a merchant who cannot see their view count should still be able
   * to manage their plan. Blocking the billing page on an analytics query
   * would trade a real capability for a nice-to-have.
   */
  getUsageAnalytics: async (): Promise<UsageAnalytics | null> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId
      ? `/api/me/usage-analytics?shop_id=${shopId}`
      : '/api/me/usage-analytics';
    try {
      const { data } = await client.get<UsageAnalytics>(url);
      return data;
    } catch (error) {
      console.error('Failed to load usage analytics:', error);
      return null;
    }
  },

  /**
   * The single highest-priority active lifecycle nudge, or null.
   *
   * Null is the normal, most common answer — a merchant at 30% of quota has no
   * nudge. Failures also resolve to null rather than throwing: this drives a
   * banner, and a merchant should never be shown a billing error because an
   * upsell resolver hiccuped.
   */
  getNudge: async (): Promise<LifecycleNudge | null> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId ? `/api/me/nudge?shop_id=${shopId}` : '/api/me/nudge';
    try {
      const { data } = await client.get<NudgeResponse>(url);
      return data?.nudge ?? null;
    } catch (error) {
      console.error('Failed to load lifecycle nudge:', error);
      return null;
    }
  },

  /**
   * The shop's AR Live Guarantee status, or null when it has never subscribed.
   *
   * Null is a legitimate answer, not an error: a shop that has never paid has
   * no frozen target to be measured against. Failures also resolve to null so
   * the caller has a single "nothing to promise" branch — the alternative is
   * rendering a half-empty guarantee claim, which is worse than rendering none.
   */
  getGuarantee: async (): Promise<GuaranteeStatus | null> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId ? `/api/me/guarantee?shop_id=${shopId}` : '/api/me/guarantee';
    try {
      const { data } = await client.get<GuaranteeResponse>(url);
      return data?.guarantee ?? null;
    } catch (error) {
      console.error('Failed to load AR Live Guarantee status:', error);
      return null;
    }
  },

  /**
   * "Not now" for the current billing period. The period is resolved
   * server-side, so there is nothing to send but the trigger key.
   */
  dismissNudge: async (triggerKey: NudgeTriggerKey): Promise<NudgeDismissResponse> => {
    const client = getApiClient();
    const shopId = getShopId();
    const base = `/api/me/nudge/${encodeURIComponent(triggerKey)}/dismiss`;
    const url = shopId ? `${base}?shop_id=${shopId}` : base;
    const { data } = await client.post<NudgeDismissResponse>(url);
    return data;
  },

  /**
   * Record the cancel-intent exit interview (§9 item L).
   *
   * NEVER throws and never returns an error to the caller. The merchant is on
   * their way to Shopify to cancel; if this POST fails, the only honest thing
   * to do is drop the answer, because surfacing an error here would read as
   * "your cancellation failed" and strand someone who has already decided to
   * leave. Resolves false when nothing was stored, purely for logging.
   */
  submitCancellationFeedback: async (
    payload: CancellationFeedbackPayload,
  ): Promise<boolean> => {
    try {
      const client = getApiClient();
      const shopId = getShopId();
      const url = shopId
        ? `/api/me/cancellation-feedback?shop_id=${shopId}`
        : '/api/me/cancellation-feedback';
      const { data } = await client.post<CancellationFeedbackResult>(url, payload);
      return data?.recorded === true;
    } catch (error) {
      console.error('Failed to record cancellation feedback:', error);
      return false;
    }
  },

  createCheckoutSession: async (
    planCode?: string,
    interval?: 'monthly' | 'annual',
  ): Promise<{ checkout_url: string }> => {
    const client = getApiClient();
    const shopId = getShopId();
    const params = new URLSearchParams();
    if (shopId) params.set('shop_id', shopId);
    if (planCode) params.set('plan_code', planCode);
    // Only sent when annual — the backend defaults to monthly.
    if (interval === 'annual') params.set('interval', 'annual');
    const query = params.toString();
    const url = query ? `/api/billing/checkout?${query}` : '/api/billing/checkout';
    const { data } = await client.post<{ checkout_url: string }>(url);
    return data;
  },

  createExtraProductPackCheckout: async (): Promise<{ checkout_url: string }> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId
      ? `/api/billing/extra-pack/products?shop_id=${shopId}`
      : '/api/billing/extra-pack/products';
    const { data } = await client.post<{ checkout_url: string }>(url);
    return data;
  },

  createPremiumGenerationUpgradeCheckout: async (): Promise<{ checkout_url: string }> => {
    const client = getApiClient();
    const shopId = getShopId();
    const url = shopId
      ? `/api/billing/extra-pack/premium?shop_id=${shopId}`
      : '/api/billing/extra-pack/premium';
    const { data } = await client.post<{ checkout_url: string }>(url);
    return data;
  },
};
